import {
  computed,
  getCurrentScope,
  inject,
  onScopeDispose,
  type ComputedRef,
  type InjectionKey,
} from "vue";

/**
 * Master's notifications for a hosted app (I-2915). The shell holds the one
 * store of the signed-in account's notifications (the bell); an app that
 * shows some of them in its own way — the calendar's reminder dialog — reads
 * them through this channel and acts on them through it, so the bell, every
 * tab and every device agree. Connect provides the channel under
 * `connectNotificationsKey`; an app uses `useConnectNotifications` and never
 * imports the shell.
 */

/** A notification as the channel hands it to an app: master's row, the fields an app may read. */
export interface ConnectNotification {
  id: number;
  /** The producing module's key (`calendar`). */
  moduleKey?: string;
  /** `<module key>.<name>` (`calendar.reminder`). */
  type?: string;
  /** The producer's English fallback texts. */
  title?: string;
  body?: string;
  /** The app-neutral link `<module key>/<resource type>/<resource id>[?query]`. */
  link?: string;
  resourceType?: string;
  /** The producer's id of what the notification is about (calendar: the CalendarEvent id). */
  resourceID?: string;
  /** The producer's values (calendar: `summary`, `start`, `end`, `allDay`, `location`, …). */
  data?: unknown;
  /** When the notification became visible. */
  notifyAt?: string;
  /** The master company it belongs to; 0 = none. */
  companyID?: number;
}

/** Which notifications a subscription holds: one module, optionally only some of its types. */
export interface ConnectNotificationFilter {
  /** The producing module's key (`calendar`). */
  moduleKey: string;
  /** Full type keys (`calendar.reminder`); none or empty = every type of the module. */
  types?: readonly string[];
}

export interface ConnectNotificationSubscription {
  /**
   * The notifications of the filter that are shown now: unread, due, of the
   * session's company or of none, and not switched off for this device type
   * — what the bell holds, in the order they became visible (oldest first). A
   * notification leaves the list when it is read, snoozed, resolved or
   * deleted, here or anywhere else (the bell, another tab, another device).
   */
  readonly items: ComputedRef<readonly ConnectNotification[]>;
  /** Ends the subscription; the list stays as it was. */
  stop(): void;
}

/** What the shell offers the apps of master's notifications. */
export interface ConnectNotificationChannel {
  subscribe(filter: ConnectNotificationFilter): ConnectNotificationSubscription;
  /** Marks notifications read on every device; false when it failed. */
  markRead(ids: readonly number[]): Promise<boolean>;
  /** „Später erinnern": hides one until `until` on every device; false when it failed. */
  snooze(id: number, until: Date): Promise<boolean>;
}

export const connectNotificationsKey: InjectionKey<ConnectNotificationChannel> =
  Symbol("connectNotifications");

/** Whether a notification belongs to a filter. */
export function matchesConnectNotificationFilter(
  row: Pick<ConnectNotification, "moduleKey" | "type">,
  filter: ConnectNotificationFilter,
): boolean {
  if (!filter.moduleKey || row.moduleKey !== filter.moduleKey) return false;
  if (!filter.types?.length) return true;
  return !!row.type && filter.types.includes(row.type);
}

const empty = computed<readonly ConnectNotification[]>(() => []);

/** Outside the shell: nothing to show, nothing to act on. */
const noChannel: ConnectNotificationChannel = {
  subscribe: () => ({ items: empty, stop: () => undefined }),
  markRead: async () => false,
  snooze: async () => false,
};

export interface UseConnectNotifications {
  readonly items: ComputedRef<readonly ConnectNotification[]>;
  markRead(ids: readonly number[]): Promise<boolean>;
  snooze(id: number, until: Date): Promise<boolean>;
}

/**
 * The notifications of one module (and type) the shell shows now, with the
 * actions on them. The subscription ends with the calling scope (the
 * component that asked).
 */
export function useConnectNotifications(
  filter: ConnectNotificationFilter,
): UseConnectNotifications {
  const channel = inject(connectNotificationsKey, noChannel);
  const subscription = channel.subscribe(filter);
  if (getCurrentScope()) onScopeDispose(() => subscription.stop());
  return {
    items: subscription.items,
    markRead: (ids) => channel.markRead(ids),
    snooze: (id, until) => channel.snooze(id, until),
  };
}
